"use client";

import { useEffect, useRef, useState, useTransition } from "react";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { ArrowUpRight, CalendarDays, Clock, Search, X } from "lucide-react";
import type { BlogPage } from "@/lib/blog";
import { blogCover } from "@/lib/images";
import { cn, formatDate } from "@/lib/utils";
import { Pagination } from "@/components/portal/Pagination";
import { Cover } from "@/components/ui/Cover";
import { Avatar } from "@/components/ui/Avatar";
import { RevealGroup, RevealItem } from "@/components/ui/Reveal";
import { Select } from "@/components/ui/Select";

/**
 * The URL is the source of truth: search text, filters, sort and page live in the query string,
 * and the server (blog/page.tsx → /api/blogs/browse) returns exactly the matching slice.
 */
export function BlogExplorer({ data }: { data: BlogPage }) {
  const router = useRouter();
  const pathname = usePathname();
  const params = useSearchParams();
  const [pending, startTransition] = useTransition();
  const topRef = useRef<HTMLDivElement>(null);

  const category = params.get("category") ?? "";
  const sort = params.get("sort") ?? "";
  const urlQuery = params.get("q") ?? "";
  const [query, setQuery] = useState(urlQuery);

  // keep the box in step when the URL changes from outside (Back button, Clear)
  useEffect(() => setQuery(urlQuery), [urlQuery]);

  function update(changes: Record<string, string>, { keepPage = false } = {}) {
    const next = new URLSearchParams(params.toString());
    for (const [k, v] of Object.entries(changes)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    if (!keepPage) next.delete("page"); // a new filter always starts from page 1
    const qs = next.toString();
    startTransition(() => router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false }));
  }

  // search runs on the server, a moment after typing stops
  useEffect(() => {
    if (query.trim() === urlQuery) return;
    const t = setTimeout(() => update({ q: query.trim() }), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- update() is rebuilt each render; only the typed text should retrigger
  }, [query]);

  function goToPage(p: number) {
    update({ page: p > 1 ? String(p) : "" }, { keepPage: true });
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function clearAll() {
    setQuery("");
    startTransition(() => router.replace(pathname, { scroll: false }));
  }

  const activeCount = [category, sort, urlQuery].filter(Boolean).length;

  return (
    <div ref={topRef} className="scroll-mt-28">
      {/* Toolbar */}
      <div className="rounded-[var(--radius-card)] border border-line bg-white p-4 shadow-soft sm:p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full lg:max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search articles, topics…"
              aria-label="Search articles"
              className="h-11 w-full rounded-xl border border-line bg-cream/50 pl-10 pr-3 text-sm text-ink outline-none transition focus:border-brand-500 focus:bg-white focus:ring-2 focus:ring-brand-600/15"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <FilterSelect
              label="Sort"
              value={sort}
              onChange={(v) => update({ sort: v })}
              options={[
                { value: "", label: "Default" },
                { value: "newest", label: "Newest" },
                { value: "oldest", label: "Oldest" },
              ]}
            />
          </div>
        </div>

        {activeCount > 0 && (
          <div className="mt-4 flex border-t border-line pt-4">
            <button
              onClick={clearAll}
              className="ml-auto inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-medium text-brand-700 hover:bg-brand-600/10"
            >
              <X className="h-3.5 w-3.5" /> Clear ({activeCount})
            </button>
          </div>
        )}
      </div>

      {/* Results */}
      <p className="mt-6 text-sm text-ink-muted" aria-live="polite">
        Showing <span className="font-semibold text-ink">{data.total}</span> {data.total === 1 ? "article" : "articles"}
      </p>

      <div className={cn("transition-opacity", pending && "opacity-50")} aria-busy={pending}>
        {data.posts.length > 0 ? (
          <>
            <RevealGroup
              key={`${category}|${sort}|${urlQuery}|${data.page}`}
              className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
            >
              {data.posts.map((post) => (
                <RevealItem key={post.slug} className="h-full">
                  <Link
                    href={`/blog/${post.slug}`}
                    className="group flex h-full flex-col overflow-hidden rounded-[var(--radius-card)] border border-line bg-white shadow-soft transition-all hover:-translate-y-1 hover:border-brand-600/30 hover:shadow-lift"
                  >
                    <div className="overflow-hidden">
                      <Cover plain fit="cover" accent={post.cover} photo={post.photo ?? blogCover(post.slug)} className="aspect-[400/250] w-full transition-transform duration-500 group-hover:scale-[1.04]" />
                    </div>
                    <div className="flex flex-1 flex-col p-5">
                      <h3 className="font-display text-lg font-semibold leading-snug tracking-tight text-ink group-hover:text-brand-700">
                        {post.title}
                      </h3>
                      <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-ink-soft">{post.excerpt}</p>
                      <div className="mt-5 flex items-center gap-2 border-t border-line pt-4 text-xs text-ink-muted">
                        {post.author && (
                          <>
                            <Avatar name={post.author} size="sm" />
                            <span className="truncate font-semibold text-ink">{post.author}</span>
                          </>
                        )}
                        <span className="inline-flex shrink-0 items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> {formatDate(post.date)}</span>
                        <span className="hidden shrink-0 items-center gap-1 sm:inline-flex"><Clock className="h-3.5 w-3.5" /> {post.readTime}</span>
                        <ArrowUpRight className="ml-auto h-4 w-4 shrink-0 text-brand-600 opacity-0 transition-opacity group-hover:opacity-100" />
                      </div>
                    </div>
                  </Link>
                </RevealItem>
              ))}
            </RevealGroup>
            {data.pageCount > 1 && (
              <Pagination
                page={data.page}
                pageSize={data.pageSize}
                total={data.total}
                shown={data.posts.length}
                onPage={goToPage}
                className="mt-10"
              />
            )}
          </>
        ) : (
          <div className="mt-6 flex flex-col items-center justify-center rounded-[var(--radius-card)] border border-dashed border-line bg-white/60 px-6 py-16 text-center">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-600/8 text-brand-600">
              <Search className="h-7 w-7" />
            </div>
            <h3 className="mt-4 font-display text-xl font-semibold text-ink">No articles found</h3>
            <p className="mt-1 max-w-sm text-sm text-ink-soft">
              {activeCount > 0 ? "We couldn’t find a match. Try a different keyword or clear your filters." : "No articles published yet - check back soon."}
            </p>
            {activeCount > 0 && (
              <button
                onClick={clearAll}
                className="mt-5 inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
              >
                <X className="h-4 w-4" /> Clear filters
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label className="inline-flex items-center gap-2 text-sm">
      <span className="text-ink-muted">{label}</span>
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 max-w-48 rounded-lg border border-line bg-cream/50 px-3 text-sm font-medium text-ink outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-600/15"
      >
        {options.map((o) => (
          <option key={o.label} value={o.value}>
            {o.label}
          </option>
        ))}
      </Select>
    </label>
  );
}
